import React, { useEffect, useState, useRef, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { POISelector } from './POISelector';
import { ChevronDownIcon, ChevronUpIcon, LayersIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
// Fix for Leaflet marker icons
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
const accommodationIcon = defaultIcon;
const createPOIIcon = iconUrl => {
  return L.icon({
    iconUrl: iconUrl,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};
// Component to handle zoom control visibility
const ZoomControlManager = ({
  sidebarOpen
}) => {
  const map = useMap();
  useEffect(() => {
    // Check if we're on mobile (screen width < 768px)
    const isMobile = window.innerWidth < 768;
    if (isMobile && sidebarOpen) {
      // Remove zoom control on mobile when sidebar is open
      map.zoomControl?.remove();
    } else {
      // Add zoom control back if it doesn't exist
      if (!map.zoomControl) {
        L.control.zoom({
          position: 'topleft'
        }).addTo(map);
      }
    }
  }, [sidebarOpen, map]);
  return null;
};
// This component updates the map when a new accommodation is hovered
const MapUpdater = ({
  hoveredAccommodation,
  accommodations
}) => {
  const map = useMap();
  useEffect(() => {
    if (hoveredAccommodation !== null) {
      const accommodation = accommodations.find(a => a.id === hoveredAccommodation);
      if (accommodation) {
        map.flyTo([accommodation.lat, accommodation.lng], 15, {
          animate: true,
          duration: 1
        });
      }
    }
  }, [hoveredAccommodation, map, accommodations]);
  return null;
};
export const Map = ({
  accommodations,
  selectedPOIs,
  poiCategories,
  togglePOI,
  hoveredAccommodation,
  sidebarOpen
}) => {
  const [showPOISelector, setShowPOISelector] = useState(false);
  const poiSelectorRef = useRef<HTMLDivElement>(null);
  const center = accommodations.length > 0 ? [accommodations[0].lat, accommodations[0].lng] : [51.505, -0.09];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (poiSelectorRef.current && !poiSelectorRef.current.contains(event.target as Node)) {
        setShowPOISelector(false);
      }
    };
    if (showPOISelector) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPOISelector]);
  return <div className="relative h-full w-full" data-id="element-134">
      <MapContainer center={center} zoom={13} zoomControl={true} style={{
      height: '100%',
      width: '100%'
    }} data-id="element-135">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-136" />
        <ZoomControlManager sidebarOpen={sidebarOpen} data-id="element-137" />
        {accommodations.map(accommodation => <Marker key={accommodation.id} position={[accommodation.lat, accommodation.lng]} icon={accommodationIcon} data-id="element-138">
            <Popup data-id="element-139">
              <div className="w-48" data-id="element-140">
                <img src={accommodation.image} alt={accommodation.title} className="w-full h-32 object-cover rounded-lg mb-2" data-id="element-141" />
                <div className="font-semibold" data-id="element-142">{accommodation.title}</div>
                <div className="text-sm text-gray-600 mb-2" data-id="element-143">
                  ${accommodation.price} / night
                </div>
                <Link to={`/accommodation/${accommodation.id}`} data-id="element-144">
                  <button className="bg-rose-500 text-white px-3 py-1 rounded-lg hover:bg-rose-600 transition w-full text-sm" data-id="element-145">
                    View Details
                  </button>
                </Link>
              </div>
            </Popup>
          </Marker>)}
        {poiCategories.map(category => {
        if (!selectedPOIs.includes(category.type)) return null;
        const poiIcon = createPOIIcon(category.icon);
        return category.items.map((poi, index) => <Marker key={`${category.type}-${index}`} position={[poi.lat, poi.lng]} icon={poiIcon} data-id="element-146">
              <Popup data-id="element-147">
                <div className="font-semibold" data-id="element-148">{poi.name}</div>
                <div className="text-sm text-gray-600" data-id="element-149">{category.label}</div>
              </Popup>
            </Marker>);
      })}
        <MapUpdater hoveredAccommodation={hoveredAccommodation} accommodations={accommodations} data-id="element-150" />
      </MapContainer>
      <div className="absolute top-4 right-4 z-[1000]" ref={poiSelectorRef} data-id="element-151">
        <button onClick={() => setShowPOISelector(prev => !prev)} className="bg-white p-2 rounded-lg shadow-md flex items-center gap-2 hover:bg-gray-50" data-id="element-152">
          <LayersIcon size={18} data-id="element-153" />
          <span className="text-sm font-medium" data-id="element-154">Points of Interest</span>
          {showPOISelector ? <ChevronUpIcon size={18} data-id="element-155" /> : <ChevronDownIcon size={18} data-id="element-156" />}
        </button>
        {showPOISelector && <div className="mt-2" data-id="element-157">
            <POISelector poiCategories={poiCategories} selectedPOIs={selectedPOIs} togglePOI={togglePOI} data-id="element-158" />
          </div>}
      </div>
    </div>;
};